import { Philately } from '$modules/pages/philately/philately';
import { Badge, BadgeTypes } from '$shared/components/badge/badge';
import { Breadcrumb } from '$shared/components/breadcrumbs/breadcrumbs.component';
import { Attach } from './attach';
import { Block } from './block';
import { Service } from './service';

export interface MetaTag {
  id?: number;
  name: string;
  content: string;
  language?: string;
}

export class Page {
  public id: number;
  public name?: string;
  public pages: any[];
  public thumbnail_image: Attach;
  public hero_image: Attach | null;
  public title: string;
  public short_content: string;
  public content: string | null;
  public meta_tags: MetaTag[];
  public search_tags: string[];
  public attaches: any[];
  public pretty_url: string;
  public blocks: Block[];
  public interesting_pages: Page[];
  public published_at: string;
  public status: null | 'new' | 'bookmark';
  public icon: Attach;
  public breadcrumbs: Breadcrumb[];
  public badge?: Badge;
  public service: Service | null;
  public children: Partial<Page[]>;
  public status_code?: number;
  public external_url: string | null;
  public link_pages: Partial<Page>[];
  public preview_category_id: number;
  public isParsed?: boolean;
  public template_type?: 'philately-with-categories';
  public philately_items?: Philately.Item[];
  public philately_grids?: Philately.GridItem[];
  public tree_title: string | null;

  public get category() {
    if (!this.breadcrumbs || this.breadcrumbs.length < 2) {
      return null;
    }

    return this.breadcrumbs[this.breadcrumbs.length - 2];
  }

  public get isBlogUrl(): boolean {
    if (!this.pretty_url || this.external_url) {
      return false;
    }
    if (
      this.pretty_url.startsWith('http://') ||
      this.pretty_url.startsWith('https://')
    ) {
      return true;
    }
    return false;
  }

  constructor(data: Page) {
    Object.assign(this, data);
    if (!data) {
      return;
    }

    if (data.blocks) {
      this.blocks = data.blocks.map((block) => new Block(block));
    }
    if (data.interesting_pages) {
      this.interesting_pages = data.interesting_pages.map(
        (page) => new Page(page)
      );
    }
    if (data.status) {
      this.badge = this.makeBadge();
    }
    if (data.content && !data.isParsed) {
      this.content = data.content
        .replace(/\n+/g, '')
        .replace(/<table.*?>(.+?)<\/table>/g, (table) => {
          return `<div class="table-responsive">${table}</div>`;
        });
      if (data.link_pages && data.link_pages.length) {
        this.replaceInnnerPagesLinks(data);
      }
      data.isParsed = true;
    }
  }

  private replaceInnnerPagesLinks(data: Page) {
    this.content = data.content.replace(
      /<a .*?href=['""](.+?)['""].*?>(.+?)<\/a>/g,
      (link, $1) => {
        if (link.includes('data-type="page-link"')) {
          if (link.includes('data-external="true"')) {
            return link;
          }
          let queryParams: string = $1.match(/[\?|#].*/) || '';
          if (queryParams) {
            queryParams = queryParams[0];
          }
          const dataIDMatch = link.match(/data-id="([^"]*)"/);
          if (!dataIDMatch || dataIDMatch.length < 2) {
            return;
          }
          const id: number = +dataIDMatch[1];
          if (!id) {
            return link;
          }
          const page = data.link_pages.find((i) => i.id === id);
          if (!page) {
            return link;
          }
          return link.replace($1, page.pretty_url + queryParams);
        }
        return link;
      }
    );
  }

  public getMetaTag(name: string): string {
    if (!this.meta_tags) {
      return '';
    }
    const found = this.meta_tags.find((tag) => tag.name === name);
    if (!found) {
      return;
    } else {
      return found.content;
    }
  }

  public setMetaTag(name: string, content: string) {
    const found = this.meta_tags.find((tag) => tag.name === name);
    if (!found) {
      this.meta_tags.push({
        name,
        content,
      });
    } else {
      found.content = content;
    }
  }

  public makeBadge() {
    let badge: Badge;

    switch (this.status) {
      case 'new':
        badge = new Badge({ type: BadgeTypes.Triangle, text: 'IS_NEW' });
        break;

      case 'bookmark':
        badge = new Badge({ type: BadgeTypes.Bookmark, text: 'IS_BOOKMARK' });
        break;
    }

    return badge;
  }
}
